import { FilterNamesEnum } from "pages/project-dashboard/models";
import { AgGridReact } from "ag-grid-react";
import {
  ESubmittalStatus,
  EWorkflowStatusDataBlock,
  MaterialNotReleaseStatus
} from "../constants";

const getFilterAllValues = async (
  gridRef: React.RefObject<AgGridReact<any>>,
  colId: string
) => {
  const filter: any = await gridRef.current?.api.getColumnFilterInstance(colId);
  if (filter?.handler?.valueModel)
    return Array.from(filter?.handler?.valueModel?.allValues, ([key]) => key);
  return [];
};

export async function applySubmittalLogFilters(params: {
  gridRef: React.RefObject<AgGridReact<any>>;
  loggedInUserInfo: any;
  submittalListFilter: any;
  SubmittalStatusToStrMap: any;
  additionalData?: any;
}) {
  const {
    gridRef,
    loggedInUserInfo,
    submittalListFilter,
    SubmittalStatusToStrMap,
    additionalData
  } = params;

  if (!gridRef.current) return;
  const applyFilter = async (
    colId: string,
    values: Array<string>,
    other?: any
  ) => {
    gridRef.current?.api?.setColumnFilterModel(colId, { values, ...other });
  };

  switch (submittalListFilter.key) {
    case "gc-due-in-days":
      applyFilter("planned_submittal_distributed_date", ["Due in 5 days"]);
      applyFilter("state", ["Draft", "Open"]);
      break;
    case "gc-overdue":
      applyFilter("planned_submittal_distributed_date", ["Overdue"]);
      applyFilter("state", ["Draft", "Open"]);
      break;
    case "gc-draft":
      applyFilter("state", ["Draft"]);
      applyFilter("submittal_created_by", [
        `${loggedInUserInfo["first-name"]} ${loggedInUserInfo["last-name"]}`
      ]);
      break;
    case "gc-to-review":
      applyFilter("assignee", [
        `${loggedInUserInfo["first-name"]} ${loggedInUserInfo["last-name"]}`
      ]);
      applyFilter("state", [
        SubmittalStatusToStrMap[ESubmittalStatus.GC_REVIEW]
      ]);
      break;
    case "gc-to-distribute":
      applyFilter("assignee", [
        `${loggedInUserInfo["first-name"]} ${loggedInUserInfo["last-name"]}`
      ]);
      applyFilter("state", [
        SubmittalStatusToStrMap[ESubmittalStatus.READY_FOR_DISTRIBUTION]
      ]);
      break;
    case "due-in-days":
      applyFilter("planned_trade_partner_submitted_date", ["Due in 5 days"]);
      applyFilter("state", ["Draft", "Open"]);
      break;
    case "due-in-days-for-sc":
      applyFilter("risk_level", ["HIGH"]);
      break;
    case "overdue":
      applyFilter("planned_trade_partner_submitted_date", ["Overdue"]);
      applyFilter("state", ["Draft", "Open"]);
      break;
    case "draft":
      applyFilter("state", ["Draft"]);
      break;
    case "to-be-submitted":
      applyFilter("assignee", [
        `${loggedInUserInfo["first-name"]} ${loggedInUserInfo["last-name"]}`
      ]);
      applyFilter("state", [SubmittalStatusToStrMap[ESubmittalStatus.SUBMIT]]);
      break;
    case "arch-due-in-days":
      applyFilter("planned_ext_review_completed_date", ["Due in 5 days"]);
      applyFilter("state", ["Draft", "Open"]);
      break;
    case "arch-overdue":
      applyFilter("planned_ext_review_completed_date", ["Overdue"]);
      applyFilter("state", ["Draft", "Open"]);
      break;
    case "arch-to-review":
      applyFilter("assignee", [
        `${loggedInUserInfo["first-name"]} ${loggedInUserInfo["last-name"]}`
      ]);
      applyFilter("workflow_status", [
        SubmittalStatusToStrMap[ESubmittalStatus.EXTERNAL_REVIEW]
      ]);
      break;
    case FilterNamesEnum.DueIn2Week:
      {
        gridRef.current?.api?.applyColumnState({
          state: [{ colId: "next_planned_deadline", sort: "asc" }],
          defaultState: {
            sort: null
          }
        });
        applyFilter("next_planned_deadline", ["Overdue or due in 2 weeks"]);
        const allValues = await getFilterAllValues(gridRef, "status");
        applyFilter(
          "status",
          allValues.filter(
            (value: string) =>
              value !== SubmittalStatusToStrMap[ESubmittalStatus.DONE] &&
              value !== SubmittalStatusToStrMap[ESubmittalStatus.VOID]
          )
        );
      }
      break;
    case FilterNamesEnum.SCDueIn2Week:
      {
        gridRef.current?.api?.applyColumnState({
          state: [{ colId: "next_planned_deadline", sort: "asc" }],
          defaultState: {
            sort: null
          }
        });
        applyFilter("next_planned_deadline", ["Overdue or due in 2 weeks"]);
        if (additionalData?.loggedInUserCompany) {
          applyFilter("responsible_contractor", [
            additionalData.loggedInUserCompany
          ]);
        }
        const allValues = await getFilterAllValues(gridRef, "status");
        applyFilter(
          "status",
          allValues.filter(
            (value: string) =>
              value !== SubmittalStatusToStrMap[ESubmittalStatus.DONE] &&
              value !== SubmittalStatusToStrMap[ESubmittalStatus.VOID]
          )
        );
      }
      break;
    case FilterNamesEnum.HighDueIn2Week:
      {
        applyFilter("due_date", ["Overdue or due in 2 weeks"]);
        applyFilter("risk_level", ["HIGH"]);
        const allValues = await getFilterAllValues(gridRef, "status");
        applyFilter(
          "status",
          allValues.filter(
            (value: string) =>
              value !== SubmittalStatusToStrMap[ESubmittalStatus.DONE] &&
              value !== SubmittalStatusToStrMap[ESubmittalStatus.VOID]
          )
        );
      }
      break;
    case FilterNamesEnum.SCHighDueIn2Week:
      {
        applyFilter("due_date", ["Overdue or due in 2 weeks"]);
        applyFilter("risk_level", ["HIGH"]);
        if (additionalData?.loggedInUserCompany) {
          applyFilter("responsible_contractor", [
            additionalData.loggedInUserCompany
          ]);
        }
        const allValues = await getFilterAllValues(gridRef, "status");
        applyFilter(
          "status",
          allValues.filter(
            (value: string) =>
              value !== SubmittalStatusToStrMap[ESubmittalStatus.DONE] &&
              value !== SubmittalStatusToStrMap[ESubmittalStatus.VOID]
          )
        );
      }
      break;
    case FilterNamesEnum.LowDueIn2Week:
      {
        applyFilter("due_date", ["Overdue or due in 2 weeks"]);
        applyFilter("risk_level", ["LOW", "MEDIUM"]);
        const allValues = await getFilterAllValues(gridRef, "status");
        applyFilter(
          "status",
          allValues.filter(
            (value: string) =>
              value !== SubmittalStatusToStrMap[ESubmittalStatus.DONE] &&
              value !== SubmittalStatusToStrMap[ESubmittalStatus.VOID]
          )
        );
      }
      break;
    case FilterNamesEnum.SCLowDueIn2Week:
      {
        applyFilter("due_date", ["Overdue or due in 2 weeks"]);
        applyFilter("risk_level", ["LOW", "MEDIUM"]);
        if (additionalData?.loggedInUserCompany) {
          applyFilter("responsible_contractor", [
            additionalData.loggedInUserCompany
          ]);
        }
        const allValues = await getFilterAllValues(gridRef, "status");
        applyFilter(
          "status",
          allValues.filter(
            (value: string) =>
              value !== SubmittalStatusToStrMap[ESubmittalStatus.DONE] &&
              value !== SubmittalStatusToStrMap[ESubmittalStatus.VOID]
          )
        );
      }
      break;
    case FilterNamesEnum.HighDueLater:
      {
        applyFilter("due_date", ["due after 2 weeks"]);
        applyFilter("risk_level", ["HIGH"]);
        const allValues = await getFilterAllValues(gridRef, "status");
        applyFilter(
          "status",
          allValues.filter(
            (value: string) =>
              value !== SubmittalStatusToStrMap[ESubmittalStatus.DONE] &&
              value !== SubmittalStatusToStrMap[ESubmittalStatus.VOID]
          )
        );
      }
      break;
    case FilterNamesEnum.SCHighDueLater:
      {
        applyFilter("due_date", ["due after 2 weeks"]);
        applyFilter("risk_level", ["HIGH"]);
        if (additionalData?.loggedInUserCompany) {
          applyFilter("responsible_contractor", [
            additionalData.loggedInUserCompany
          ]);
        }
        const allValues = await getFilterAllValues(gridRef, "status");
        applyFilter(
          "status",
          allValues.filter(
            (value: string) =>
              value !== SubmittalStatusToStrMap[ESubmittalStatus.DONE] &&
              value !== SubmittalStatusToStrMap[ESubmittalStatus.VOID]
          )
        );
      }
      break;
    case FilterNamesEnum.LowDueLater:
      {
        applyFilter("due_date", ["due after 2 weeks"]);
        applyFilter("risk_level", ["LOW", "MEDIUM"]);
        const allValues = await getFilterAllValues(gridRef, "status");
        applyFilter(
          "status",
          allValues.filter(
            (value: string) =>
              value !== SubmittalStatusToStrMap[ESubmittalStatus.DONE] &&
              value !== SubmittalStatusToStrMap[ESubmittalStatus.VOID]
          )
        );
      }
      break;
    case FilterNamesEnum.SCLowDueLater:
      {
        applyFilter("due_date", ["due after 2 weeks"]);
        applyFilter("risk_level", ["LOW", "MEDIUM"]);
        if (additionalData?.loggedInUserCompany) {
          applyFilter("responsible_contractor", [
            additionalData.loggedInUserCompany
          ]);
        }
        const allValues = await getFilterAllValues(gridRef, "status");
        applyFilter(
          "status",
          allValues.filter(
            (value: string) =>
              value !== SubmittalStatusToStrMap[ESubmittalStatus.DONE] &&
              value !== SubmittalStatusToStrMap[ESubmittalStatus.VOID]
          )
        );
      }
      break;
    case "alert_new_submittals":
      {
        applyFilter("created_at", ["after date"], {
          dateFrom: submittalListFilter?.value
        });

        const allValues = await getFilterAllValues(gridRef, "status");
        applyFilter(
          "status",
          allValues.filter(
            (value: string) =>
              value !== SubmittalStatusToStrMap[ESubmittalStatus.VOID]
          )
        );
      }
      break;
    case FilterNamesEnum.NoPlan:
      {
        applyFilter("next_planned_deadline", ["no date present"]);
        const allValues = await getFilterAllValues(gridRef, "status");
        applyFilter(
          "status",
          allValues.filter(
            (value: string) =>
              value !== SubmittalStatusToStrMap[ESubmittalStatus.DONE] &&
              value !== SubmittalStatusToStrMap[ESubmittalStatus.VOID]
          )
        );
      }
      break;
    default:
      console.log("no filters matched");
  }

  setTimeout(() => {
    gridRef?.current?.api?.onFilterChanged();
  }, 0);
}

export async function applyMaterialLogFilters(params: {
  gridRef: React.RefObject<AgGridReact<any>>;
  materialListFilter: any;
  additionalData?: any;
}) {
  const { gridRef, materialListFilter, additionalData } = params;
  if (!gridRef.current) return;

  const applyFilter = async (colId: string, values: Array<string>) => {
    gridRef.current?.api?.setColumnFilterModel(colId, { values });
  };

  switch (materialListFilter.key) {
    case "is_linked_void_submittal":
      applyFilter("is_linked_void_submittal", ["Voided Submittals"]);
      break;
    case "gc-due-in-days":
      applyFilter("planned_ROJ", ["Due in 5 days"]);
      applyFilter("actual_ROJ", ["Actual Date NA"]);
      break;
    case "gc-overdue":
      applyFilter("planned_ROJ", ["Overdue"]);
      applyFilter("actual_ROJ", ["Actual Date NA"]);
      break;
    case "due-in-days":
      applyFilter("planned_ROJ", ["Due in 5 days"]);
      applyFilter("actual_ROJ", ["Actual Date NA"]);
      break;
    case "due-in-days-for-sc":
      applyFilter("risk_level", ["HIGH"]);
      break;
    case "overdue":
      applyFilter("planned_ROJ", ["Overdue"]);
      applyFilter("actual_ROJ", ["Actual Date NA"]);
      break;
    case FilterNamesEnum.DueIn2Week: {
      gridRef.current?.api?.applyColumnState({
        state: [{ colId: "next_planned_deadline", sort: "asc" }],
        defaultState: {
          sort: null
        }
      });
      const allValues = await getFilterAllValues(gridRef, "workflow_status");
      applyFilter(
        "workflow_status",
        allValues.filter(
          (value: string) =>
            value !== EWorkflowStatusDataBlock.WF_COMPLETED &&
            value !== EWorkflowStatusDataBlock.VOID
        )
      );
      applyFilter("next_planned_deadline", ["Overdue or due in 2 weeks"]);
      break;
    }
    case FilterNamesEnum.SCDueIn2Week: {
      gridRef.current?.api?.applyColumnState({
        state: [{ colId: "next_planned_deadline", sort: "asc" }],
        defaultState: {
          sort: null
        }
      });
      const allValues = await getFilterAllValues(gridRef, "workflow_status");
      applyFilter(
        "workflow_status",
        allValues.filter(
          (value: string) =>
            value !== EWorkflowStatusDataBlock.WF_COMPLETED &&
            value !== EWorkflowStatusDataBlock.VOID
        )
      );
      if (additionalData?.loggedInUserCompany) {
        applyFilter("trade_partner", [additionalData.loggedInUserCompany]);
      }
      applyFilter("next_planned_deadline", ["Overdue or due in 2 weeks"]);
      break;
    }
    case FilterNamesEnum.HighDueIn2Week: {
      applyFilter("due_date", ["Overdue or due in 2 weeks"]);
      applyFilter("risk_level", ["HIGH"]);
      const allValues = await getFilterAllValues(gridRef, "workflow_status");
      applyFilter(
        "workflow_status",
        allValues.filter(
          (value: string) =>
            value !== EWorkflowStatusDataBlock.WF_COMPLETED &&
            value !== EWorkflowStatusDataBlock.VOID
        )
      );

      break;
    }
    case FilterNamesEnum.SCHighDueIn2Week: {
      applyFilter("due_date", ["Overdue or due in 2 weeks"]);
      applyFilter("risk_level", ["HIGH"]);
      const allValues = await getFilterAllValues(gridRef, "workflow_status");
      applyFilter(
        "workflow_status",
        allValues.filter(
          (value: string) =>
            value !== EWorkflowStatusDataBlock.WF_COMPLETED &&
            value !== EWorkflowStatusDataBlock.VOID
        )
      );
      if (additionalData?.loggedInUserCompany) {
        applyFilter("trade_partner", [additionalData.loggedInUserCompany]);
      }
      break;
    }
    case FilterNamesEnum.LowDueIn2Week: {
      applyFilter("due_date", ["Overdue or due in 2 weeks"]);
      applyFilter("risk_level", ["LOW", "MEDIUM"]);
      const allValues = await getFilterAllValues(gridRef, "workflow_status");
      applyFilter(
        "workflow_status",
        allValues.filter(
          (value: string) =>
            value !== EWorkflowStatusDataBlock.WF_COMPLETED &&
            value !== EWorkflowStatusDataBlock.VOID
        )
      );
      break;
    }
    case FilterNamesEnum.SCLowDueIn2Week: {
      applyFilter("due_date", ["Overdue or due in 2 weeks"]);
      applyFilter("risk_level", ["LOW", "MEDIUM"]);
      const allValues = await getFilterAllValues(gridRef, "workflow_status");
      applyFilter(
        "workflow_status",
        allValues.filter(
          (value: string) =>
            value !== EWorkflowStatusDataBlock.WF_COMPLETED &&
            value !== EWorkflowStatusDataBlock.VOID
        )
      );
      if (additionalData?.loggedInUserCompany) {
        applyFilter("trade_partner", [additionalData.loggedInUserCompany]);
      }
      break;
    }
    case FilterNamesEnum.HighDueLater: {
      applyFilter("due_date", ["due after 2 weeks"]);
      applyFilter("risk_level", ["HIGH"]);
      const allValues = await getFilterAllValues(gridRef, "workflow_status");
      applyFilter(
        "workflow_status",
        allValues.filter(
          (value: string) =>
            value !== EWorkflowStatusDataBlock.WF_COMPLETED &&
            value !== EWorkflowStatusDataBlock.VOID
        )
      );
      break;
    }
    case FilterNamesEnum.SCHighDueLater: {
      applyFilter("due_date", ["due after 2 weeks"]);
      applyFilter("risk_level", ["HIGH"]);
      const allValues = await getFilterAllValues(gridRef, "workflow_status");
      applyFilter(
        "workflow_status",
        allValues.filter(
          (value: string) =>
            value !== EWorkflowStatusDataBlock.WF_COMPLETED &&
            value !== EWorkflowStatusDataBlock.VOID
        )
      );
      if (additionalData?.loggedInUserCompany) {
        applyFilter("trade_partner", [additionalData.loggedInUserCompany]);
      }
      break;
    }
    case FilterNamesEnum.LowDueLater: {
      applyFilter("due_date", ["due after 2 weeks"]);
      applyFilter("risk_level", ["LOW", "MEDIUM"]);
      const allValues = await getFilterAllValues(gridRef, "workflow_status");
      applyFilter(
        "workflow_status",
        allValues.filter(
          (value: string) =>
            value !== EWorkflowStatusDataBlock.WF_COMPLETED &&
            value !== EWorkflowStatusDataBlock.VOID
        )
      );
      break;
    }
    case FilterNamesEnum.SCLowDueLater: {
      applyFilter("due_date", ["due after 2 weeks"]);
      applyFilter("risk_level", ["LOW", "MEDIUM"]);
      const allValues = await getFilterAllValues(gridRef, "workflow_status");
      applyFilter(
        "workflow_status",
        allValues.filter(
          (value: string) =>
            value !== EWorkflowStatusDataBlock.WF_COMPLETED &&
            value !== EWorkflowStatusDataBlock.VOID
        )
      );
      if (additionalData?.loggedInUserCompany) {
        applyFilter("trade_partner", [additionalData.loggedInUserCompany]);
      }
      break;
    }
    case FilterNamesEnum.NoPlan:
      applyFilter("next_planned_deadline", ["no date present"]);
      applyFilter("status", [MaterialNotReleaseStatus]);
      break;
    default:
      console.log("no filters matched");
  }
  setTimeout(() => {
    gridRef?.current?.api?.onFilterChanged();
  }, 0);
}

export function applyBidPackagesLogFilters(params: {
  gridRef: React.RefObject<AgGridReact<any>>;
  bidPackageListFilter: any;
}) {
  const { gridRef, bidPackageListFilter } = params;
  if (!gridRef.current) return;

  const applyFilter = async (colId: string, values: Array<string>) => {
    gridRef.current?.api?.setColumnFilterModel(colId, { values });
  };

  switch (bidPackageListFilter?.key) {
    case FilterNamesEnum.DueIn2Week:
      gridRef.current?.api?.applyColumnState({
        state: [{ colId: "next_planned_deadline", sort: "asc" }],
        defaultState: {
          sort: null
        }
      });
      applyFilter("next_planned_deadline", ["Overdue or due in 2 weeks"]);
      break;
    default:
      console.log("no filters matched");
  }

  setTimeout(() => {
    gridRef?.current?.api?.onFilterChanged();
  }, 0);
}
